import { Badge, Box, Flex, Group, Paper, Stack, Text, Title } from "@mantine/core";
import { IconEye } from "@tabler/icons-react";
import { ObjectivePhoriaResult } from "../../../types/testTypes";
import {
  PhoriaShiftMagnitude,
  PhoriaSpreadSeverity,
  buildPhoriaClinicalSummary,
  classifyPhoriaSpread,
  describeHorizontal,
  describeVertical,
  formatPhoriaPd,
  getPhoriaColor,
  getPhoriaShift,
  hasAnyUnreliableAxis,
  isHorizontalReliable,
  isVerticalReliable,
} from "../utils/phoriaCalculations";
import { AgeBand, isCriticalPeriod } from "../utils/lazyEyeRiskModel";
import { RISK_LEVEL_COLOR } from "@constants/resultColors";
import { RiskLevel } from "../utils/severity";

interface LazyEyePhoriaCardProps {
  phoriaData: ObjectivePhoriaResult;
  ageBand: AgeBand;
  riskLevel: RiskLevel;
}

interface ConditionColumnProps {
  label: string;
  badgeColor: string;
  summary?: import("../../../types/testTypes").ObjectivePhoriaConditionSummary;
  occlusions?: ObjectivePhoriaResult["occlusions"];
}

const SPREAD_TEXT_COLOR: Record<PhoriaSpreadSeverity, string> = {
  ok: "dimmed",
  elevated: "yellow.7",
  high: "red.7",
};

function ConditionColumn({ label, badgeColor, summary, occlusions }: ConditionColumnProps) {
  const hSpread = classifyPhoriaSpread(summary?.horizontalIqrPd);
  const vSpread = classifyPhoriaSpread(summary?.verticalIqrPd);
  const hReliable = isHorizontalReliable(summary, occlusions);
  const vReliable = isVerticalReliable(summary, occlusions);

  return (
    <Paper withBorder p="sm" radius="md" style={{ flex: 1 }}>
      <Stack gap={6}>
        <Badge color={badgeColor} variant="filled" size="sm">
          {label}
        </Badge>

        <Box>
          <Text size="xs" c="dimmed">
            Horizontal
          </Text>
          <Group gap={6} align="baseline">
            {hReliable ? (
              <>
                <Text fw={600} c={getPhoriaColor(summary?.horizontalPhoriaPd)}>
                  {formatPhoriaPd(summary?.horizontalPhoriaPd)}
                </Text>
                <Text size="xs" c="dimmed">
                  {describeHorizontal(summary?.horizontalPhoriaPd)}
                </Text>
              </>
            ) : (
              <>
                <Text fw={600} c="dimmed">
                  {formatPhoriaPd(summary?.horizontalPhoriaPd)}
                </Text>
                <Text size="xs" c="red.7" fs="italic">
                  (unreliable)
                </Text>
              </>
            )}
          </Group>
        </Box>

        <Box>
          <Text size="xs" c="dimmed">
            Vertical
          </Text>
          <Group gap={6} align="baseline">
            {vReliable ? (
              <>
                <Text fw={600}>{formatPhoriaPd(summary?.verticalPhoriaPd)}</Text>
                <Text size="xs" c="dimmed">
                  {describeVertical(summary?.verticalPhoriaPd)}
                </Text>
              </>
            ) : (
              <>
                <Text fw={600} c="dimmed">
                  {formatPhoriaPd(summary?.verticalPhoriaPd)}
                </Text>
                <Text size="xs" c="red.7" fs="italic">
                  (unreliable)
                </Text>
              </>
            )}
          </Group>
        </Box>

        {Number.isFinite(summary?.horizontalIqrPd) && (
          <Text size="xs" c={SPREAD_TEXT_COLOR[hSpread]}>
            Horizontal spread (IQR): {formatPhoriaPd(summary?.horizontalIqrPd)}
            {Number.isFinite(summary?.horizontalSdPd) && (
              <Text component="span" size="xs" c="dimmed">
                {" · SD "}
                {formatPhoriaPd(summary?.horizontalSdPd)}
              </Text>
            )}
          </Text>
        )}

        {Number.isFinite(summary?.verticalIqrPd) && (
          <Text size="xs" c={SPREAD_TEXT_COLOR[vSpread]}>
            Vertical spread (IQR): {formatPhoriaPd(summary?.verticalIqrPd)}
            {Number.isFinite(summary?.verticalSdPd) && (
              <Text component="span" size="xs" c="dimmed">
                {" · SD "}
                {formatPhoriaPd(summary?.verticalSdPd)}
              </Text>
            )}
          </Text>
        )}

        {summary?.validOcclusionCount != null && (
          <Text size="xs" c="dimmed">
            Valid cycles: {summary.validOcclusionCount}
          </Text>
        )}
      </Stack>
    </Paper>
  );
}

function riskBadgeText(level: RiskLevel, ageBand: AgeBand): string {
  if (level === "high") return "OUTSIDE RANGE";
  if (level === "moderate") return isCriticalPeriod(ageBand) ? "WATCH CLOSELY" : "WATCH";
  return "WITHIN RANGE";
}

const SHIFT_PRESENTATION: Record<
  PhoriaShiftMagnitude,
  { color: string; surface: string; caption: string }
> = {
  minimal: {
    color: "green",
    surface: "var(--oculera-surface-alt)",
    caption: "Minimal — eyes essentially hold alignment between distance and near",
  },
  typical: {
    color: "blue",
    surface: "var(--oculera-surface-alt)",
    caption: "Within the normal AC/A range",
  },
  notable: {
    color: "yellow",
    surface: "var(--oculera-tint-yellow)",
    caption: "Larger than typical — worth monitoring",
  },
  marked: {
    color: "red",
    surface: "var(--oculera-tint-yellow)",
    caption: "Unusually large — warrants further evaluation",
  },
};

export function LazyEyePhoriaCard({ phoriaData, ageBand, riskLevel }: LazyEyePhoriaCardProps) {
  const distanceSummary = phoriaData.conditionSummaries?.find(
    (s) => s.conditionName === "distance",
  );
  const nearSummary = phoriaData.conditionSummaries?.find((s) => s.conditionName === "near");
  const occlusions = phoriaData.occlusions;
  const shift = getPhoriaShift(distanceSummary, nearSummary, occlusions);
  const clinicalSummary = buildPhoriaClinicalSummary(distanceSummary, nearSummary, occlusions);
  const anyUnreliable = hasAnyUnreliableAxis(phoriaData);

  return (
    <Paper shadow="sm" radius="md" p="sm" withBorder>
      <Group justify="space-between" align="flex-start" mb="md" wrap="nowrap">
        <Group gap="md" align="flex-start" style={{ flex: 1 }}>
          <Box
            style={{
              width: 48,
              height: 48,
              backgroundColor: "var(--mantine-color-cyan-6)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <IconEye size={24} color="white" />
          </Box>
          <Stack gap={4} style={{ flex: 1 }}>
            <Title order={4} mb={0}>
              Eye Alignment (Phoria)
            </Title>
            <Text size="sm" c="dimmed">
              Tendency of the eyes to deviate when fusion is briefly disrupted
            </Text>
          </Stack>
        </Group>

        {anyUnreliable ? (
          <Badge color="red" size="lg" radius="md" style={{ flexShrink: 0 }}>
            UNSTABLE READINGS
          </Badge>
        ) : (
          <Badge
            color={RISK_LEVEL_COLOR[riskLevel]}
            size="lg"
            radius="md"
            style={{ flexShrink: 0 }}
          >
            {riskBadgeText(riskLevel, ageBand)}
          </Badge>
        )}
      </Group>

      <Flex gap="sm" direction="row" wrap="nowrap">
        <ConditionColumn
          label="Distance"
          badgeColor="blue"
          summary={distanceSummary}
          occlusions={occlusions}
        />
        <ConditionColumn
          label="Near"
          badgeColor="teal"
          summary={nearSummary}
          occlusions={occlusions}
        />
      </Flex>

      {shift && (
        <Paper
          mt="sm"
          p="sm"
          withBorder
          radius="md"
          bg={SHIFT_PRESENTATION[shift.magnitude].surface}
        >
          <Group gap="sm">
            <Text size="sm" fw={600}>
              Near–Distance Shift:
            </Text>
            <Badge color={SHIFT_PRESENTATION[shift.magnitude].color} variant="filled" size="sm">
              {shift.value >= 0 ? "+" : ""}
              {formatPhoriaPd(shift.value)}
            </Badge>
            <Text size="sm" c="dimmed">
              {SHIFT_PRESENTATION[shift.magnitude].caption}
            </Text>
          </Group>
        </Paper>
      )}

      <Paper mt="sm" p="sm" withBorder radius="md">
        <Text size="sm" c="dimmed">
          <Text span fw={600}>
            What this means:{" "}
          </Text>
          {clinicalSummary}
        </Text>
      </Paper>
    </Paper>
  );
}
